import {
  BarController,
  BarElement,
  CategoryScale,
  Chart as ChartJS,
  Legend,
  LinearScale,
  LineController,
  LineElement,
  PointElement,
  Tooltip,
} from "chart.js";
import React from "react";

import type { Visualization } from "../market-ai.api";

ChartJS.register(
  BarController,
  BarElement,
  CategoryScale,
  Legend,
  LinearScale,
  LineController,
  LineElement,
  PointElement,
  Tooltip,
);

const PALETTE = [
  { background: "rgba(22,139,107,0.12)", border: "#168b6b" },
  { background: "rgba(96,166,211,0.12)", border: "#60a6d3" },
  { background: "rgba(229,87,67,0.12)", border: "#e55743" },
  { background: "rgba(245,166,35,0.12)", border: "#f5a623" },
];

type Props = { visualization: Visualization };

const MarketChart = ({ visualization }: Props) => {
  const canvasRef = React.useRef<HTMLCanvasElement>(null);
  const chartRef = React.useRef<ChartJS | null>(null);

  React.useEffect(() => {
    if (!canvasRef.current) return;
    chartRef.current?.destroy();

    const labels =
      visualization.datasets[0]?.data.map((pt) => pt.datetime) ?? [];
    const datasets = visualization.datasets.map((ds, i) => {
      const color = PALETTE[i % PALETTE.length];
      return {
        backgroundColor: color.background,
        borderColor: color.border,
        borderWidth: 2,
        data: ds.data.map((pt) => pt.rate),
        fill: visualization.type === "line",
        label: ds.label,
        pointBackgroundColor: color.border,
        pointHoverRadius: visualization.type === "line" ? 4 : undefined,
        pointRadius: visualization.type === "line" ? 0 : undefined,
        tension: visualization.type === "line" ? 0.3 : undefined,
      };
    });

    /* eslint-disable lingui/no-unlocalized-strings -- chart.js config strings */
    chartRef.current = new ChartJS(canvasRef.current, {
      data: { datasets, labels },
      options: {
        animation: { duration: 400 },
        maintainAspectRatio: false,
        plugins: {
          legend: {
            display: visualization.datasets.length > 1,
            labels: {
              boxWidth: 10,
              color: "#7a8093",
              font: { family: "Roboto, sans-serif", size: 11 },
              padding: 12,
              usePointStyle: true,
            },
            position: "top",
          },
          tooltip: {
            backgroundColor: "#fff",
            bodyColor: "#4d5570",
            bodyFont: { family: "Roboto, sans-serif", size: 12 },
            borderColor: "rgba(202,204,209,0.6)",
            borderWidth: 1,
            intersect: false,
            mode: "index",
            padding: 10,
            titleColor: "#4d5570",
            titleFont: {
              family: "Roboto, sans-serif",
              size: 11,
              weight: "bold",
            },
          },
        },
        responsive: true,
        scales: {
          x: {
            border: { color: "transparent" },
            grid: { color: "transparent" },
            ticks: {
              color: "#7a8093",
              font: { family: "Roboto, sans-serif", size: 12 },
              maxRotation: 30,
              maxTicksLimit: 8,
            },
          },
          y: {
            beginAtZero: false,
            border: { color: "transparent", dash: [4, 4] },
            grid: { color: "rgba(202,204,209,0.4)" },
            ticks: {
              color: "#7a8093",
              font: { family: "Roboto, sans-serif", size: 12 },
            },
          },
        },
      },
      type: visualization.type,
    });

    /* eslint-enable lingui/no-unlocalized-strings */
    return () => {
      chartRef.current?.destroy();
      chartRef.current = null;
    };
  }, [visualization]);

  return (
    <div
      className="overflow-hidden rounded-2xl"
      style={{
        background: "#fff",
        border: "1px solid rgba(202,204,209,0.4)",
        boxShadow: "2px 4px 12px 0px rgba(122,128,147,0.18)",
      }}
    >
      {/* Header stripe */}
      <div
        className="flex items-center gap-x-2 px-4 py-3"
        style={{
          background:
            "linear-gradient(90deg, rgba(22,139,107,0.06) 0%, transparent 100%)",
          borderBottom: "1px solid rgba(202,204,209,0.4)",
        }}
      >
        <div
          className="h-2 w-2 rounded-full"
          style={{ background: "#168b6b" }}
        />
        <h3
          className="text-sm font-semibold tracking-wide"
          style={{ color: "#4d5570" }}
        >
          {visualization.title}
        </h3>
      </div>

      {/* Chart canvas */}
      <div className="relative px-3 pb-3 pt-3" style={{ height: "260px" }}>
        <canvas ref={canvasRef} />
      </div>
    </div>
  );
};

export { MarketChart };
